<template>
  <div class="modal-overlay" @click.self="$emit('close')">
    <div class="modal-container">
      <!-- HEADER -->
      <div class="modal-header">
        <h3>Chi tiết phiếu xuất</h3>
        <div class="actions">
          <button v-if="!isEdit" @click="isEdit = true">✏️ Sửa</button>
          <button v-if="isEdit" :disabled="loading" @click="handleUpdate">
            {{ loading ? "Đang lưu..." : "💾 Lưu" }}
          </button>
          <button class="btn-close" @click="$emit('close')">×</button>
        </div>
      </div>

      <!-- BODY -->
      <div class="modal-body">
        <!-- INFO -->
        <div class="info-grid">
          <div class="info-item">
            <label>Mã phiếu</label>
            <span>{{ form.ma_phieu }}</span>
          </div>

          <div class="info-item">
            <label>Ngày xuất</label>
            <input
              :hidden="!isEdit"
              type="date"
              v-model="form.ngay_xuat_phieu"
            />
            <span :hidden="isEdit">{{ form.ngay_xuat_phieu }}</span>
          </div>

          <div class="info-item">
            <label>Loại xuất</label>
            <span class="badge" :class="form.loai_phieu">
              {{ formatType(form.loai_phieu) }}
            </span>
          </div>

          <div class="info-item">
            <label>Người xuất</label>
            <span>{{ form.nguoi_xuat || "---" }}</span>
          </div>

          <!-- <div class="info-item">
            <label for="">Xuất cho sản phẩm</label>
            <span>{{ receipt.target_product.ten_hang }}</span>
            <span>Serial/Imei: {{ receipt.target_product.serial_imei }}</span>
          </div> -->
          <div class="info-item product-info">
            <label>Xuất cho sản phẩm</label>

            <div class="product-box">
              <span class="product-name">
                {{ receipt.target_product.ten_hang }}
              </span>

              <span @click="viewProductDetail" class="product-serial">
                Serial/Imei: {{ receipt.target_product.serial_imei }}
              </span>
            </div>
          </div>
        </div>

        <!-- TABLE -->
        <div class="table-wrapper">
          <table class="table">
            <thead>
              <tr>
                <th>#</th>
                <th>Tên SP</th>
                <th>Giá</th>
                <th>SL</th>
                <th>Thành tiền</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(item, index) in receipt.items" :key="index">
                <td>{{ index + 1 }}</td>
                <td>{{ item.product.ten_hang }}</td>
                <td>{{ formatCurrency(item.gia_von_luc_xuat) }}</td>
                <td>{{ item.so_luong }}</td>
                <td>
                  {{ formatCurrency(item.gia_von_luc_xuat * item.so_luong) }}
                </td>
              </tr>

              <tr v-if="!receipt.items || receipt.items.length === 0">
                <td colspan="5" class="empty">Không có sản phẩm</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- TOTAL -->
        <div class="total">
          Tổng tiền:
          <strong>{{ formatCurrency(receipt.tong_gia_tri) }}</strong>
        </div>
      </div>

      <!-- FOOTER -->
      <div class="modal-footer">
        <button class="btn-close-2" @click="$emit('close')">Đóng</button>
      </div>
    </div>

    <ProductDetailModal
      v-if="showProductDetail"
      :product="receipt.target_product"
      @close="showProductDetail = false"
    />
  </div>
</template>

<script setup>
import { reactive, ref, toRefs, watch } from "vue";
import ProductDetailModal from "../products/ProductDetailModal.vue";
import { formatCurrency } from "@/utils/helpers";
import { useToast } from "@/composables/useToast";
import { exportReceiptApi } from "@/services/api";

const loading = ref(false);
const toast = useToast();
const isEdit = ref(false);
const showProductDetail = ref(false);
const props = defineProps({
  receipt: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["update"]);
const { receipt } = toRefs(props);
const form = reactive({});
const originalData = ref({});
watch(
  () => receipt.value,
  (val) => {
    Object.assign(form, val);
    originalData.value = JSON.parse(JSON.stringify(val));
  },
  { immediate: true, deep: true },
);
const formatType = (type) => {
  switch (type) {
    case "ban_hang":
      return "Bán hàng";
    case "noi_bo":
      return "Nội bộ";
    case "huy":
      return "Hủy";
    case "chuyen_kho":
      return "Chuyển kho";
    default:
      return type;
  }
};

const viewProductDetail = () => {
  showProductDetail.value = true;
};

const handleUpdate = async () => {
  loading.value = true;
  const receiptID = receipt.value.id;
  let hasChanged = false;
  const formData = new FormData();
  Object.keys(form).forEach((key) => {
    if (JSON.stringify(form[key]) != JSON.stringify(originalData.value[key])) {
      let value = form[key];
      if (typeof value === "boolean") value = value ? 1 : 0;
      if (value === null || value === undefined) value = "";
      formData.append(key, form[key]);
      hasChanged = true;
    }
  });
  formData.append("_method", "PUT");

  if (!hasChanged) {
    toast.info("Bạn chưa thay đổi thông tin nào");
    loading.value = false;
    return;
  }
  try {
    const response = await exportReceiptApi.updateExportReceipt(
      receiptID,
      formData,
    );
    if (response.data && response.data.success) {
      toast.success("Cập nhật phiếu xuất thành công");
      isEdit.value = false;
      Object.assign(form, response.data.data);
      emit("update");
    }
  } catch (err) {
    console.log(err);
    toast.error("Có lỗi xảy ra khi cập nhật phiếu xuất");
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
/* overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

/* container */
.modal-container {
  width: 800px;
  max-height: 90vh;
  background: #fff;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* header */
.modal-header {
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
}

.btn-close {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
}

/* body */
.modal-body {
  padding: 16px;
  overflow-y: auto;
}

/* info */
.info-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 16px;
}

.info-item {
  display: inline-grid;
  justify-content: center;
}
.info-item label {
  font-size: 12px;
  color: #6b7280;
}

.info-item span {
  font-weight: 500;
}
.export_type {
  width: fit-content;
}
/* table */
.table {
  width: 100%;
  border-collapse: collapse;
}

.table th,
.table td {
  padding: 8px;
  border-bottom: 1px solid #eee;
}

/* badge */
.badge {
  padding: 4px 8px;
  border-radius: 6px;
  color: #fff;
  font-size: 12px;
  width: fit-content;
}

.badge.ban_hang {
  background: #42b983;
}

.badge.noi_bo {
  background: #3b82f6;
}

.badge.huy {
  background: #ef4444;
}

.badge.chuyen_kho {
  background: #f59e0b;
}

/* total */
.total {
  text-align: right;
  margin-top: 10px;
  font-size: 16px;
}
.total strong {
  color: #ef4444;
}

/* footer */
.modal-footer {
  padding: 12px;
  border-top: 1px solid #eee;
  text-align: right;
}

.btn-close-2 {
  background: #e5e7eb;
  padding: 6px 12px;
  border: none;
  border-radius: 6px;
}

/* empty */
.empty {
  text-align: center;
  color: #999;
  padding: 10px;
}

.product-info .product-box {
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

/* Tên sản phẩm */
.product-name {
  font-weight: 600;
  color: #111827;
  font-size: 15px;
}

/* Serial / IMEI dạng tag */
.product-serial {
  display: inline-block;
  width: fit-content;
  padding: 4px 10px;
  font-size: 12px;
  border-radius: 999px;
  background: #eef2ff;
  color: #4338ca;
  font-weight: 500;
  cursor: pointer;
}
</style>
